<script lang="ts" setup>
import { refuge } from "~/data/refuge";
import type { MenuSchemaCategory } from "~/composables/useMenuSchema";

usePageSeo('seoMenuTitle', 'seoMenuDescription');

const { t, locale, locales, defaultLocale } = useI18n();
const switchLocalePath = useSwitchLocalePath();

// The nav switcher takes its order straight from the `locales` array in
// nuxt.config.ts, which happens to start with English. On the QR menu the
// order is a decision rather than an accident: this is a Greek refuge and `el`
// is the default locale, so Greek leads and the rest keep the config order.
const orderedLocales = computed(() => [
  ...locales.value.filter(option => option.code === defaultLocale),
  ...locales.value.filter(option => option.code !== defaultLocale),
]);

const searchQuery = ref('');
const activeCategory = ref('all');

// Shapes live in composables/useMenuSchema.ts, next to the builder that also
// consumes them, rather than being declared once here and once there.
const { data: categories } = await useFetch<MenuSchemaCategory[]>('/api/menu');

// The menu as structured data. Computed for the same reason the FAQ's graph is
// (see pages/refuge/faq.vue): the dish names are translated, and a locale
// switch is a route change into this same component.
useSchemaOrg(computed(() => {
  const menu = buildMenuSchema(categories.value, t, refuge.currency, locale.value, t('ourMenu'));
  return menu ? [menu] : [];
}));

const filteredCategories = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  const base = activeCategory.value === 'all'
      ? (categories.value || [])
      : (categories.value || []).filter(c => c.id === activeCategory.value);

  if (!query) return base;

  return base
      .map(cat => ({
        ...cat,
        items: cat.items.filter(item => t(item.key).toLowerCase().includes(query)),
      }))
      .filter(cat => cat.items.length > 0);
});

const hasResults = computed(() => filteredCategories.value.some(c => c.items.length > 0));
const resultCount = computed(() =>
    filteredCategories.value.reduce((total, category) => total + category.items.length, 0)
);

function formatPrice(price: number): string {
  return `€${price.toFixed(2)}`;
}
</script>

<template>
  <div class="menu-page">

    <!-- ── Page header ───────────────────────────────────── -->
    <section class="menu-header">
      <span class="eyebrow">
        <Icon name="ph:fork-knife" />
        {{ $t('Food & Drink') }}
      </span>
      <h1>{{ $t('ourMenu') }}</h1>
      <p class="header-subtitle">{{ $t('menuSubtitle') }}</p>

      <!-- Guests arrive here by scanning the QR code on the table, where the
           language switcher up in the nav is not somewhere they will look.
           /menu guesses their language from the browser; this is the visible
           correction for a wrong guess. -->
      <nav class="menu-languages" :aria-label="$t('chooseLanguage')">
        <Icon name="ph:globe-light" class="menu-languages-icon" aria-hidden="true" />
        <NuxtLink
            v-for="option in orderedLocales"
            :key="option.code"
            :to="switchLocalePath(option.code)"
            replace
            class="lang-pill"
            :class="{ active: option.code === locale }"
            :aria-current="option.code === locale ? 'true' : undefined"
        >
          {{ option.name }}
        </NuxtLink>
      </nav>
    </section>

    <!-- ── Controls ─────────────────────────────────────── -->
    <div class="controls-bar">
      <div class="controls-inner">

        <div class="search-wrapper">
          <Icon name="ph:magnifying-glass" class="search-icon" />
          <input
              v-model="searchQuery"
              type="search"
              :aria-label="$t('searchTheMenu')"
              :placeholder="$t('menuSearchPlaceholder')"
              class="search-input"
          />
          <button v-if="searchQuery" type="button" class="search-clear"
                  :aria-label="$t('clearSearch')" @click="searchQuery = ''">
            <Icon name="ph:x" aria-hidden="true"/>
          </button>
        </div>

        <div class="category-chips" role="group" :aria-label="$t('filterByCategory')">
          <button
              type="button"
              class="chip"
              :class="{ active: activeCategory === 'all' }"
              :aria-pressed="activeCategory === 'all'"
              @click="activeCategory = 'all'"
          >
            {{ $t('menuAllCategories') }}
          </button>
          <button
              v-for="cat in (categories || [])"
              :key="cat.id"
              type="button"
              class="chip"
              :class="{ active: activeCategory === cat.id }"
              :aria-pressed="activeCategory === cat.id"
              @click="activeCategory = cat.id"
          >
            <Icon :name="cat.icon" aria-hidden="true"/>
            <span>{{ $t(cat.labelKey) }}</span>
          </button>
        </div>

      </div>
    </div>

    <!-- ── Menu body ─────────────────────────────────────── -->
    <div class="menu-body">
      <!-- Filtering happens without a page change, so the outcome has to be
           announced, not only shown. -->
      <p class="sr-only" role="status" aria-live="polite">
        {{ hasResults ? $t('menuResultsFound', { count: resultCount }) : $t('menuNoResults') }}
      </p>

      <template v-if="hasResults">
        <section
            v-for="category in filteredCategories"
            :key="category.id"
            class="category-section"
        >
          <div class="category-heading">
            <div class="category-icon-wrap">
              <Icon :name="category.icon" aria-hidden="true"/>
            </div>
            <h2>{{ $t(category.labelKey) }}</h2>
          </div>

          <div class="items-grid">
            <div
                v-for="item in category.items"
                :key="item.key"
                class="menu-item"
            >
              <div style="display: flex; justify-content: space-between; width: 100%">
                <div class="item-left">
                  <span class="item-name">{{ $t(item.key) }}</span>
                  <span v-if="item.note" class="item-note">{{ item.note }}</span>
                </div>
                <span class="item-price">{{ formatPrice(item.price) }}</span>
              </div>

            </div>
          </div>
        </section>
      </template>

      <div v-else class="empty-state">
        <Icon name="ph:magnifying-glass" class="empty-icon" aria-hidden="true"/>
        <p>{{ $t('menuNoResults') }}</p>
      </div>
    </div>

  </div>
</template>

<style lang="sass" scoped>
// ── Tokens ────────────────────────────────────────────────────
// Colors and radii come from assets/sass/_variables.sass, which Vite injects
// into every style block. Only values with no global equivalent live here.
$primary-faint: rgba($color-primary, 0.08)
$primary-border: rgba($color-primary, 0.25)
$primary-glow: rgba($color-primary, 0.35)
$primary-focus: rgba($color-primary, 0.12)
$text-muted: rgba(0, 0, 0, 0.45)
$border: rgba(0, 0, 0, 0.08)
$surface: #f7f8f6
$radius-sm: 10px
$transition: 0.2s ease

// ── Page ──────────────────────────────────────────────────────
.menu-page
  min-height: 100vh
  background: $color-light-bg

// ── Header ────────────────────────────────────────────────────
.menu-header
  display: flex
  flex-direction: column
  align-items: center
  text-align: center
  padding: 5rem 2rem 3rem
  background: linear-gradient(to bottom, rgba($color-primary, 0.06) 0%, transparent 100%)

  h1
    margin: 0.3rem 0 0.6rem
    font-size: clamp(2.4rem, 5vw, 3.5rem)

.eyebrow
  display: inline-flex
  align-items: center
  gap: 0.4rem
  font-size: 0.72rem
  font-weight: 800
  letter-spacing: 3px
  text-transform: uppercase
  color: $color-primary

  .iconify
    font-size: 1rem

.header-subtitle
  font-size: clamp(1rem, 2vw, 1.15rem)
  color: $text-muted
  margin: 0 0 1.25rem
  max-width: 480px

// ── Language row ──────────────────────────────────────────────
.menu-languages
  display: flex
  flex-wrap: wrap
  align-items: center
  justify-content: center
  gap: 0.4rem

.menu-languages-icon
  font-size: 1.15rem
  color: $text-muted
  margin-right: 0.15rem

.lang-pill
  padding: 0.35rem 0.8rem
  font-size: 0.82rem
  font-weight: 700
  text-decoration: none
  white-space: nowrap
  background: $surface
  border: 1.5px solid $border
  border-radius: 50px
  color: $text-muted
  transition: all $transition

  &:hover
    border-color: $color-primary
    color: $color-primary

  &.active
    background: $color-primary
    border-color: $color-primary
    color: white
    box-shadow: 0 2px 12px $primary-glow

// ── Controls bar ──────────────────────────────────────────────
.controls-bar
  background: rgba(255, 255, 255, 0.92)
  border-bottom: 1px solid $border
  padding: 0.875rem 0

.controls-inner
  max-width: 960px
  margin: 0 auto
  padding: 0 2rem
  display: flex
  flex-direction: column
  gap: 0.75rem

// ── Search ────────────────────────────────────────────────────
.search-wrapper
  position: relative
  display: flex
  align-items: center

  .search-icon
    position: absolute
    left: 1rem
    font-size: 1.1rem
    color: $text-muted
    pointer-events: none

.search-input
  width: 100%
  padding: 0.7rem 2.75rem
  font-size: 0.95rem
  font-family: inherit
  font-weight: 500
  color: $color-text-main
  background: $surface
  border: 1.5px solid $border
  border-radius: $radius-sm
  outline: none
  transition: border-color $transition, box-shadow $transition
  -webkit-appearance: none

  // The browser draws its own clear glyph on type="search", which the rule
  // above does not remove, so it lands next to .search-clear and the field
  // shows two X's. The styled one stays; this one goes.
  &::-webkit-search-cancel-button
    -webkit-appearance: none
    display: none

  &::placeholder
    color: $text-muted

  &:focus
    border-color: rgba($color-primary, 0.5)
    box-shadow: 0 0 0 3px $primary-focus

.search-clear
  position: absolute
  right: 0.75rem
  background: none
  border: none
  cursor: pointer
  display: flex
  align-items: center
  justify-content: center
  color: $text-muted
  font-size: 1rem
  padding: 0.25rem
  border-radius: 50%
  transition: color $transition

  &:hover
    color: $color-text-main

// ── Category chips ────────────────────────────────────────────
.category-chips
  display: flex
  gap: 0.45rem
  overflow-x: scroll
  padding: 4px 16px 2px
  scrollbar-width: none
  -ms-overflow-style: none
  flex-wrap: wrap

  &::-webkit-scrollbar
    display: none

.chip
  display: inline-flex
  align-items: center
  gap: 0.35rem
  padding: 0.4rem 0.85rem
  font-size: 0.82rem
  font-weight: 700
  font-family: inherit
  white-space: nowrap
  background: $surface
  border: 1.5px solid $border
  border-radius: 50px
  cursor: pointer
  color: $text-muted
  transition: all $transition

  .iconify
    font-size: 0.9rem

  &:hover
    border-color: $color-primary
    color: $color-primary

  &.active
    background: $color-primary
    border-color: $color-primary
    color: white
    box-shadow: 0 2px 12px $primary-glow

// ── Menu body ─────────────────────────────────────────────────
.menu-body
  max-width: 960px
  margin: 0 auto
  padding: 3rem 2rem 5rem

// ── Category section ──────────────────────────────────────────
.category-section
  margin-bottom: 3.5rem

  &:last-child
    margin-bottom: 0

.category-heading
  display: flex
  align-items: center
  gap: 0.75rem
  margin-bottom: 1.25rem
  padding-bottom: 0.875rem
  border-bottom: 2px solid rgba($color-primary, 0.15)

  h2
    margin: 0
    font-size: 1.3rem
    font-weight: 800
    letter-spacing: -0.01em

.category-icon-wrap
  display: flex
  align-items: center
  justify-content: center
  width: 2.4rem
  height: 2.4rem
  border-radius: 10px
  background: $primary-faint
  color: $color-primary
  flex-shrink: 0

  .iconify
    font-size: 1.15rem

// ── Items grid ────────────────────────────────────────────────
.items-grid
  display: grid
  grid-template-columns: repeat(2, 1fr)
  gap: 0.5rem

.menu-item
  display: flex
  align-items: center
  gap: 0.75rem
  padding: 0.8rem 1rem
  background: $surface
  border-radius: $radius-sm
  border: 1px solid $border
  transition: border-color $transition, background $transition

  &:hover
    background: $color-light-bg
    border-color: $primary-border

.item-left
  display: flex
  flex-direction: column
  gap: 0.15rem
  min-width: 0

.item-name
  font-size: 0.92rem
  font-weight: 600
  color: $color-text-main
  line-height: 1.3
  // prevent overflow on long item names
  overflow: hidden
  display: -webkit-box
  -webkit-line-clamp: 2
  -webkit-box-orient: vertical

.item-note
  font-size: 0.72rem
  font-weight: 700
  letter-spacing: 0.5px
  color: $color-primary
  text-transform: uppercase

.item-price
  font-size: 0.95rem
  font-weight: 800
  color: $color-primary
  white-space: nowrap
  flex-shrink: 0
  font-variant-numeric: tabular-nums

// ── Empty state ───────────────────────────────────────────────
.empty-state
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  gap: 1rem
  padding: 5rem 2rem
  color: $text-muted

  .empty-icon
    font-size: 3rem
    opacity: 0.35

  p
    font-size: 1.05rem
    font-weight: 600
    margin: 0

// ── Dark mode ─────────────────────────────────────────────────
.dark-mode .menu-page
  background: $color-dark-bg

.dark-mode .menu-header
  background: linear-gradient(to bottom, rgba($color-primary, 0.05) 0%, transparent 100%)

.dark-mode .menu-header h1
  color: $color-text-light

.dark-mode .header-subtitle
  color: rgba(255, 255, 255, 0.45)

.dark-mode .menu-languages-icon
  color: rgba(255, 255, 255, 0.3)

.dark-mode .lang-pill
  background: $color-surface-dark
  border-color: rgba(255, 255, 255, 0.08)
  color: rgba(255, 255, 255, 0.45)

.dark-mode .lang-pill:hover
  border-color: $color-primary
  color: $color-primary

.dark-mode .controls-bar
  background: rgba(30, 30, 30, 0.92)
  border-bottom-color: rgba(255, 255, 255, 0.07)

.dark-mode .search-input
  background: $color-surface-dark
  border-color: rgba(255, 255, 255, 0.08)
  color: $color-text-light

.dark-mode .search-input::placeholder
  color: rgba(255, 255, 255, 0.3)

.dark-mode .search-input:focus
  border-color: rgba($color-primary, 0.45)
  box-shadow: 0 0 0 3px rgba($color-primary, 0.08)

.dark-mode .search-icon
  color: rgba(255, 255, 255, 0.3)

.dark-mode .search-clear
  color: rgba(255, 255, 255, 0.3)

.dark-mode .search-clear:hover
  color: $color-text-light

.dark-mode .chip
  background: $color-surface-dark
  border-color: rgba(255, 255, 255, 0.08)
  color: rgba(255, 255, 255, 0.45)

.dark-mode .chip:hover
  border-color: $color-primary
  color: $color-primary

.dark-mode .category-heading
  border-bottom-color: rgba($color-primary, 0.12)

.dark-mode .category-heading h2
  color: $color-text-light

.dark-mode .category-icon-wrap
  background: rgba($color-primary, 0.07)

.dark-mode .menu-item
  background: $color-surface-dark
  border-color: rgba(255, 255, 255, 0.06)

.dark-mode .menu-item:hover
  background: rgb(40, 40, 40)
  border-color: rgba($color-primary, 0.25)

.dark-mode .item-name
  color: $color-text-light

.dark-mode .empty-state
  color: rgba(255, 255, 255, 0.3)

// ── Responsive ────────────────────────────────────────────────
@media (max-width: 640px)
  .items-grid
    grid-template-columns: 1fr

  .menu-header
    // Keep the header content clear of the fixed 70px mobile nav bar
    padding: 5.5rem 1.25rem 2rem

  .controls-inner
    padding: 0 1.25rem

  .menu-body
    padding: 2rem 1.25rem 4rem
</style>